import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getKilnImage, convertUnits, returnUnit } from './utils/kiln-card-utils';

/**
 * @param {Object} props A prop drilled down from LogKiln.js
 * @param {(kilnDetails: {Object}) => void} props.setKilnDetails This sets the current kilnDetails state to be added to the project list.
 * @param {Object} props.kilnDetails Listens to changes to kilnDetails, and resets selected kiln card if submission was successful.
 * @param {string} props.variant "Square" || "Rectangle" || "Polygon"
 * @param {number} props.isActive Value to determine which card is active
 * @param {(isActive: number) => void} props.setIsActive Function to set the active Umptopia Kiln
 * @returns {JSX.Element} Returns a Kiln Card component used in Flame Cap Kiln -> Project Info. The card has preset values to help streamline
 * the user's dimension selection process.
 */

export default function UmptopiaCard({ setKilnDetails, kilnDetails, variant, isActive, setIsActive }) {
    const { selectedProject } = useSelector(state => state.postFormData);
    const currentProjectMeasurementSystem = selectedProject.measureType

    // listen to changes in kilnDetails, and reset if submission has taken place
    useEffect(() => {
        if (kilnDetails.kilnId === null) {
            setIsActive(null)
        }
    }, [kilnDetails.kilnId])

    const kilnCardDetailsFontSize = 18

    const umptopiaPanelConstants = {
        kilnType: 'Umptopia',
        units: 'feet',
        heightOfInnerPanels: convertUnits(3.33, 'Imperial', currentProjectMeasurementSystem),
        heatShieldPanelLength: convertUnits(4, 'Imperial', currentProjectMeasurementSystem),
        heatShieldPanelWidth: convertUnits(4, 'Imperial', currentProjectMeasurementSystem),
    }

    /**
     * 
     * @param {string} numberOfSides i.e. '5'
     * @returns {string} '5 x 4 ft sides, 3.33 ft high'
     */
    const generateIrregularKilnDimensions = (numberOfSides) => {
        return `${numberOfSides} x 4 ft sides, 3.33 ft high`
    }

    const kilnCardObjects = [
        {
            kilnId: "4_panel_square",
            numberOfPanels: 4,
            shape: 'Square',
            kilnLength: 4,
            kilnWidth: 4,
            ...umptopiaPanelConstants,
        },
        {
            kilnId: "8_panel_square",
            numberOfPanels: 8,
            shape: 'Square',
            kilnLength: 8,
            kilnWidth: 8,
            ...umptopiaPanelConstants,
        },
        {
            kilnId: "6_panel_rectangle",
            numberOfPanels: 6,
            shape: 'Rectangle',
            kilnLength: 4,
            kilnWidth: 8,
            ...umptopiaPanelConstants,
        },
        {
            kilnId: "8_panel_rectangle",
            numberOfPanels: 8,
            shape: 'Rectangle',
            kilnLength: 4,
            kilnWidth: 12,
            ...umptopiaPanelConstants,
        },
        {
            kilnId: "10_panel_rectangle",
            numberOfPanels: 10,
            shape: 'Rectangle',
            kilnLength: 8,
            kilnWidth: 12,
            ...umptopiaPanelConstants,
        },
        {
            kilnId: "5_panel_pentagon",
            numberOfPanels: 5,
            shape: 'Pentagon',
            ...umptopiaPanelConstants,
        },
        {
            kilnId: "6_panel_hexagon",
            numberOfPanels: 6,
            shape: 'Hexagon',
            ...umptopiaPanelConstants,
        },
        {
            kilnId: "7_panel_heptagon",
            numberOfPanels: 7,
            shape: 'Heptagon',
            ...umptopiaPanelConstants,
        },
        {
            kilnId: "8_panel_octagon",
            numberOfPanels: 8,
            shape: 'Octagon',
            ...umptopiaPanelConstants,
        },
    ]

    return (
        <Box sx={{
            width: '100%',
            maxWidth: '1800px',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            rowGap: 3,
            columnGap: 1,
            p: 4
        }}>
            {kilnCardObjects.map((kilnCardObject, index) => {
                if (kilnCardObject.shape === variant
                    || kilnCardObject.shape.includes('gon') && variant === 'Polygon')
                    return (
                        <Box key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: 'fit-content',
                                textAlign: 'left',
                                color: '#000',
                                border: '1px solid #000',
                                borderRadius: '5px',
                                padding: 2,
                                cursor: 'pointer',
                                '&:hover': {
                                    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                                },
                                ...(isActive === index && {
                                    background: 'linear-gradient(to bottom right, #535988, #313551)',
                                    color: '#FFF', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px'
                                })

                            }}
                            onClick={() => {
                                setIsActive(index);
                                setKilnDetails(kilnCardObject);
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 3, gap: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>Umptopia II:</Typography>
                                    <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>
                                        {kilnCardObject.numberOfPanels} Panel {kilnCardObject.shape}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <img
                                        src={getKilnImage(variant, kilnCardObject.shape)}
                                        alt='Kiln'
                                        width={100}
                                        style={{ objectFit: 'cover' }} />
                                </Box>
                            </Box>
                            {!kilnCardObject.shape.includes('gon')
                                ? <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Kiln Length: </Typography>
                                        <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>{kilnCardObject.kilnLength}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Kiln Width: </Typography>
                                        <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>{kilnCardObject.kilnWidth}</Typography>
                                    </Box>
                                </Box>
                                : <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Kiln Dimensions: </Typography>
                                        <Typography sx={{ fontSize: kilnCardDetailsFontSize, textAlign: 'center' }}>
                                            {generateIrregularKilnDimensions(kilnCardObject.numberOfPanels)}
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Height of Inner Panels: </Typography>
                                <Typography sx={{ fontSize: kilnCardDetailsFontSize, fontWeight: 'bold' }}>
                                    {kilnCardObject.heightOfInnerPanels} {returnUnit(kilnCardObject.units)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Length of Inner Panels:</Typography>
                                <Typography sx={{ fontSize: kilnCardDetailsFontSize, fontWeight: 'bold' }}>
                                    {kilnCardObject.heatShieldPanelLength} {returnUnit(kilnCardObject.units)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Width of Inner Panels:</Typography>
                                <Typography sx={{ fontSize: kilnCardDetailsFontSize, fontWeight: 'bold' }}>
                                    {kilnCardObject.heatShieldPanelWidth} {returnUnit(kilnCardObject.units)}
                                </Typography>
                            </Box>
                        </Box>
                    )
            })}
        </Box>
    )
}
